/**
 * Every sound is synthesized with WebAudio, so the app ships no audio files and every sound can be
 * layered, pitched and timed against the animation. The context is created on the first sound
 * (always after a tap), which satisfies mobile autoplay rules.
 */
export type Cue =
    | 'ui'
    | 'start'
    | 'pause'
    | 'poke'
    | 'equip'
    | 'anticipate'
    | 'explode'
    | 'roll'
    | 'bounce'
    | 'launch'
    | 'land'
    | 'respawn'
    | 'fanfare'
    | 'chime'
    | 'wheee'
    | 'hihi'
    | 'ooh'
    | 'alarm'
    | 'yawn';

interface VoiceOptions {
    /** Pitch path as [seconds, Hz] points. */
    pitch: [number, number][];
    /** The vowel over time as [seconds, formant 1 Hz, formant 2 Hz] points. */
    vowel: [number, number, number][];
    dur: number;
    gain?: number;
    /** Wobble speed in Hz and depth as a fraction of the pitch. */
    vibrato?: number;
    vibratoDepth?: number;
    delay?: number;
    /** Breathiness: a little shaped noise mixed in, 0..1. */
    breath?: number;
    /** Starts faint and muffled and gets louder and brighter, like something coming closer. */
    approach?: boolean;
    /** Where the voice goes; the master output by default. Lets a long voice be stopped. */
    bus?: AudioNode;
}

interface ToneOptions {
    freq: number;
    freqEnd?: number;
    dur: number;
    type?: OscillatorType;
    gain?: number;
    delay?: number;
    /** Attack in seconds. */
    attack?: number;
}

interface NoiseOptions {
    dur: number;
    from: number;
    to?: number;
    gain?: number;
    delay?: number;
    filter?: BiquadFilterType;
    q?: number;
}

export class SoundEngine {
    private ctx: AudioContext | null = null;
    private master: GainNode | null = null;
    private noiseBuffer: AudioBuffer | null = null;
    muted = false;

    private ensure(): AudioContext | null {
        if (this.ctx) {
            if (this.ctx.state === 'suspended') void this.ctx.resume();
            return this.ctx;
        }
        const Ctor: typeof AudioContext | undefined =
            window.AudioContext ??
            (window as unknown as { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext;
        if (!Ctor) return null;
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.7;
        this.master.connect(this.ctx.destination);

        const length = this.ctx.sampleRate * 2;
        this.noiseBuffer = this.ctx.createBuffer(
            1,
            length,
            this.ctx.sampleRate
        );
        const data = this.noiseBuffer.getChannelData(0);
        for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
        return this.ctx;
    }

    private tone({
        freq,
        freqEnd,
        dur,
        type = 'sine',
        gain = 0.3,
        delay = 0,
        attack = 0.005,
    }: ToneOptions) {
        const ctx = this.ctx!;
        const t0 = ctx.currentTime + delay;
        const osc = ctx.createOscillator();
        const amp = ctx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(freq, t0);
        if (freqEnd)
            osc.frequency.exponentialRampToValueAtTime(
                Math.max(1, freqEnd),
                t0 + dur
            );
        amp.gain.setValueAtTime(0.0001, t0);
        amp.gain.exponentialRampToValueAtTime(gain, t0 + attack);
        amp.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
        osc.connect(amp).connect(this.master!);
        osc.start(t0);
        osc.stop(t0 + dur + 0.05);
    }

    private noise({
        dur,
        from,
        to,
        gain = 0.4,
        delay = 0,
        filter = 'lowpass',
        q = 0.8,
    }: NoiseOptions) {
        const ctx = this.ctx!;
        const t0 = ctx.currentTime + delay;
        const src = ctx.createBufferSource();
        src.buffer = this.noiseBuffer;
        src.loop = true;
        const f = ctx.createBiquadFilter();
        f.type = filter;
        f.Q.value = q;
        f.frequency.setValueAtTime(from, t0);
        if (to)
            f.frequency.exponentialRampToValueAtTime(
                Math.max(20, to),
                t0 + dur
            );
        const amp = ctx.createGain();
        amp.gain.setValueAtTime(gain, t0);
        amp.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
        src.connect(f).connect(amp).connect(this.master!);
        src.start(t0);
        src.stop(t0 + dur + 0.05);
    }

    /** A cartoon "voice": a buzzy oscillator shaped by two moving vowel formants, with a wobble. */
    private voice({
        pitch,
        vowel,
        dur,
        gain = 0.5,
        vibrato = 6,
        vibratoDepth = 0.03,
        delay = 0,
        breath = 0.08,
        approach = false,
        bus,
    }: VoiceOptions) {
        const ctx = this.ctx!;
        const t0 = ctx.currentTime + delay;
        const end = t0 + dur;

        // The voice box: a buzzy wave, slightly detuned in pairs so it is not a pure synth tone.
        const oscs = [0, 1].map((i) => {
            const osc = ctx.createOscillator();
            osc.type = i === 0 ? 'sawtooth' : 'square';
            osc.detune.value = i === 0 ? -4 : 5;
            osc.frequency.setValueAtTime(pitch[0][1], t0);
            pitch.slice(1).forEach(([t, f]) => {
                osc.frequency.linearRampToValueAtTime(f, t0 + t);
            });
            return osc;
        });
        const mix = ctx.createGain();
        mix.gain.value = 0.5;
        oscs[0].connect(mix);
        const squareLevel = ctx.createGain();
        squareLevel.gain.value = 0.35;
        oscs[1].connect(squareLevel).connect(mix);

        // Vibrato plus a faster, smaller wobble so the pitch is never perfectly steady.
        [
            { hz: vibrato, depth: vibratoDepth },
            { hz: vibrato * 2.7, depth: vibratoDepth * 0.35 },
        ].forEach(({ hz, depth }) => {
            const lfo = ctx.createOscillator();
            const lfoGain = ctx.createGain();
            lfo.frequency.value = hz;
            lfoGain.gain.value = pitch[0][1] * depth;
            lfo.connect(lfoGain);
            oscs.forEach((o) => lfoGain.connect(o.frequency));
            lfo.start(t0);
            lfo.stop(end + 0.05);
        });

        const amp = ctx.createGain();
        if (approach) {
            amp.gain.setValueAtTime(gain * 0.04, t0);
            amp.gain.exponentialRampToValueAtTime(
                gain,
                t0 + Math.max(0.05, dur - 0.1)
            );
            amp.gain.exponentialRampToValueAtTime(0.0001, end);
        } else {
            amp.gain.setValueAtTime(0.0001, t0);
            amp.gain.exponentialRampToValueAtTime(gain, t0 + dur * 0.12);
            amp.gain.setValueAtTime(gain, t0 + dur * 0.65);
            amp.gain.exponentialRampToValueAtTime(0.0001, end);
        }

        // Distance: far away is dull, close is bright.
        const air = ctx.createBiquadFilter();
        air.type = 'lowpass';
        if (approach) {
            air.frequency.setValueAtTime(600, t0);
            air.frequency.exponentialRampToValueAtTime(
                9000,
                t0 + Math.max(0.05, dur - 0.1)
            );
        } else {
            air.frequency.value = 12000;
        }
        amp.connect(air).connect(bus ?? this.master!);

        // Two vowel formants that glide, so the sound opens and closes like a mouth.
        [0, 1].forEach((i) => {
            const f = ctx.createBiquadFilter();
            f.type = 'bandpass';
            f.Q.value = i === 0 ? 5 : 8;
            f.frequency.setValueAtTime(vowel[0][i + 1], t0);
            vowel.slice(1).forEach((point) => {
                f.frequency.linearRampToValueAtTime(
                    point[i + 1],
                    t0 + point[0]
                );
            });
            const level = ctx.createGain();
            level.gain.value = i === 0 ? 1 : 0.7;
            mix.connect(f).connect(level).connect(amp);
        });

        if (breath > 0) {
            const src = ctx.createBufferSource();
            src.buffer = this.noiseBuffer;
            src.loop = true;
            const f = ctx.createBiquadFilter();
            f.type = 'bandpass';
            f.frequency.value = vowel[0][2];
            f.Q.value = 0.8;
            const level = ctx.createGain();
            level.gain.value = breath;
            src.connect(f).connect(level).connect(amp);
            src.start(t0);
            src.stop(end + 0.05);
        }

        oscs.forEach((o) => {
            o.start(t0);
            o.stop(end + 0.05);
        });
    }

    private screamBus: GainNode | null = null;

    /** "AAAAAAA" heard from far away for `seconds`, getting closer and louder until it ends. */
    scream(seconds: number) {
        if (this.muted || seconds < 0.5 || !this.ensure()) return;
        this.stopScream();
        const bus = this.ctx!.createGain();
        bus.connect(this.master!);
        this.screamBus = bus;
        this.voice({
            pitch: [
                [0, 400],
                [seconds * 0.5, 520],
                [seconds * 0.96, 780],
            ],
            vowel: [
                [0, 700, 1150],
                [seconds, 900, 1400],
            ],
            dur: seconds,
            gain: 0.55,
            vibrato: 6,
            vibratoDepth: 0.045,
            breath: 0.14,
            approach: true,
            bus,
        });
    }

    /** Cuts the scream short (the timer was paused, reset or skipped). */
    stopScream() {
        const bus = this.screamBus;
        if (!bus || !this.ctx) return;
        this.screamBus = null;
        bus.gain.setTargetAtTime(0, this.ctx.currentTime, 0.03);
        window.setTimeout(() => bus.disconnect(), 400);
    }

    play(cue: Cue, pitch = 1) {
        if (this.muted) return;
        if (!this.ensure()) return;
        const p = pitch;
        switch (cue) {
            case 'ui':
                this.tone({
                    freq: 660 * p,
                    freqEnd: 880 * p,
                    dur: 0.07,
                    type: 'triangle',
                    gain: 0.18,
                });
                break;
            case 'start':
                this.tone({
                    freq: 392 * p,
                    freqEnd: 520 * p,
                    dur: 0.12,
                    type: 'square',
                    gain: 0.12,
                });
                this.tone({
                    freq: 660 * p,
                    freqEnd: 990 * p,
                    dur: 0.18,
                    type: 'square',
                    gain: 0.12,
                    delay: 0.09,
                });
                this.noise({
                    dur: 0.2,
                    from: 800,
                    to: 5000,
                    gain: 0.08,
                    filter: 'bandpass',
                });
                break;
            case 'pause':
                this.tone({
                    freq: 520 * p,
                    freqEnd: 300 * p,
                    dur: 0.14,
                    type: 'triangle',
                    gain: 0.2,
                });
                break;
            case 'poke':
                // A rubbery boing: a falling sweep with vibrato from a second oscillator.
                this.tone({
                    freq: 420 * p,
                    freqEnd: 150 * p,
                    dur: 0.38,
                    type: 'sine',
                    gain: 0.35,
                    attack: 0.01,
                });
                this.tone({
                    freq: 840 * p,
                    freqEnd: 300 * p,
                    dur: 0.3,
                    type: 'triangle',
                    gain: 0.1,
                });
                break;
            case 'equip':
                this.tone({
                    freq: 500 * p,
                    freqEnd: 1100 * p,
                    dur: 0.09,
                    type: 'sine',
                    gain: 0.3,
                });
                this.tone({
                    freq: 1400 * p,
                    dur: 0.08,
                    type: 'triangle',
                    gain: 0.1,
                    delay: 0.07,
                });
                break;
            case 'anticipate':
                this.tone({
                    freq: 160,
                    freqEnd: 1400,
                    dur: 0.75,
                    type: 'sawtooth',
                    gain: 0.12,
                    attack: 0.2,
                });
                this.tone({
                    freq: 320,
                    freqEnd: 2200,
                    dur: 0.75,
                    type: 'sine',
                    gain: 0.12,
                    attack: 0.3,
                });
                break;
            case 'explode':
                this.noise({ dur: 0.9, from: 4000, to: 80, gain: 0.9 });
                this.tone({
                    freq: 140,
                    freqEnd: 35,
                    dur: 0.6,
                    type: 'sine',
                    gain: 0.9,
                    attack: 0.002,
                });
                // The wet splat: band-passed noise that drops in pitch.
                this.noise({
                    dur: 0.45,
                    from: 1200,
                    to: 180,
                    gain: 0.5,
                    filter: 'bandpass',
                    q: 3,
                    delay: 0.03,
                });
                this.tone({
                    freq: 220,
                    freqEnd: 60,
                    dur: 0.3,
                    type: 'sawtooth',
                    gain: 0.25,
                    delay: 0.05,
                });
                break;
            case 'roll':
                this.noise({
                    dur: 1.5,
                    from: 250,
                    to: 700,
                    gain: 0.35,
                    filter: 'bandpass',
                    q: 1.5,
                });
                this.tone({
                    freq: 220,
                    freqEnd: 880,
                    dur: 1.2,
                    type: 'triangle',
                    gain: 0.12,
                    attack: 0.3,
                });
                break;
            case 'bounce':
                this.tone({
                    freq: 190 * p,
                    freqEnd: 70 * p,
                    dur: 0.16,
                    type: 'sine',
                    gain: 0.5,
                    attack: 0.002,
                });
                break;
            case 'launch':
                this.noise({ dur: 1.8, from: 300, to: 6000, gain: 0.7 });
                this.tone({
                    freq: 90,
                    freqEnd: 900,
                    dur: 1.6,
                    type: 'sawtooth',
                    gain: 0.22,
                    attack: 0.1,
                });
                this.tone({
                    freq: 45,
                    freqEnd: 200,
                    dur: 1.6,
                    type: 'square',
                    gain: 0.18,
                    attack: 0.1,
                });
                break;
            case 'land':
                this.tone({
                    freq: 130,
                    freqEnd: 45,
                    dur: 0.4,
                    type: 'sine',
                    gain: 0.8,
                    attack: 0.002,
                });
                this.noise({ dur: 0.25, from: 1500, to: 150, gain: 0.4 });
                break;
            case 'respawn':
                this.tone({
                    freq: 380,
                    freqEnd: 1000,
                    dur: 0.14,
                    type: 'sine',
                    gain: 0.35,
                });
                this.tone({
                    freq: 1000,
                    freqEnd: 1500,
                    dur: 0.1,
                    type: 'triangle',
                    gain: 0.15,
                    delay: 0.08,
                });
                break;
            case 'fanfare': {
                const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5];
                notes.forEach((f, i) =>
                    this.tone({
                        freq: f,
                        dur: 0.22,
                        type: 'triangle',
                        gain: 0.22,
                        delay: i * 0.085,
                    })
                );
                this.tone({
                    freq: 1046.5,
                    dur: 0.7,
                    type: 'square',
                    gain: 0.07,
                    delay: 0.42,
                });
                this.tone({
                    freq: 1318.5,
                    dur: 0.7,
                    type: 'triangle',
                    gain: 0.12,
                    delay: 0.42,
                });
                this.noise({
                    dur: 0.4,
                    from: 6000,
                    to: 9000,
                    gain: 0.08,
                    filter: 'highpass',
                    delay: 0.4,
                });
                break;
            }
            case 'wheee':
                // A genuinely happy "Weeeee!": a quick "w", then a bouncing, swooping "ee".
                this.voice({
                    pitch: [
                        [0, 380 * p],
                        [0.14, 560 * p],
                        [0.45, 1000 * p],
                        [0.8, 1250 * p],
                        [1.05, 1120 * p],
                        [1.5, 760 * p],
                    ],
                    vowel: [
                        [0, 320, 780],
                        [0.14, 300, 2450],
                        [1.5, 320, 2750],
                    ],
                    dur: 1.5,
                    gain: 0.4,
                    vibrato: 6.5,
                    vibratoDepth: 0.035,
                    breath: 0.05,
                });
                break;
            case 'hihi': {
                // A cute giggle: three quick breathy "hi"s, each a little higher than the last.
                [0, 0.15, 0.3].forEach((delay, i) => {
                    const top = (1150 + i * 140) * p;
                    this.voice({
                        pitch: [
                            [0, top * 0.8],
                            [0.05, top],
                            [0.12, top * 0.9],
                        ],
                        vowel: [
                            [0, 400, 1700],
                            [0.05, 300, 2500],
                            [0.12, 320, 2600],
                        ],
                        dur: 0.12,
                        gain: 0.3,
                        breath: 0.35,
                        delay,
                    });
                });
                break;
            }
            case 'alarm': {
                // The finish when nobody can see the tomato: three bright chimes, loud enough to hear from
                // another room, repeated so a single missed ding does not matter.
                for (let i = 0; i < 3; i++) {
                    const t = i * 0.95;
                    this.tone({
                        freq: 880,
                        dur: 0.4,
                        type: 'triangle',
                        gain: 0.5,
                        delay: t,
                    });
                    this.tone({
                        freq: 1318.5,
                        dur: 0.5,
                        type: 'triangle',
                        gain: 0.45,
                        delay: t + 0.16,
                    });
                    this.tone({
                        freq: 1760,
                        dur: 0.6,
                        type: 'sine',
                        gain: 0.3,
                        delay: t + 0.32,
                    });
                }
                break;
            }
            case 'ooh':
                // "Oouuuuh": a spooky, slowly falling "oo" that opens into "uh".
                this.voice({
                    pitch: [
                        [0, 330 * p],
                        [0.5, 360 * p],
                        [1.3, 200 * p],
                    ],
                    vowel: [
                        [0, 300, 800],
                        [1.3, 550, 1000],
                    ],
                    dur: 1.3,
                    gain: 0.45,
                    vibrato: 5,
                });
                break;
            case 'yawn':
                // "Aaaah-hum": a wide open "ah" that sags and closes, over a breath of noise.
                this.voice({
                    pitch: [
                        [0, 260 * p],
                        [0.5, 300 * p],
                        [1.6, 150 * p],
                    ],
                    vowel: [
                        [0, 800, 1200],
                        [1.6, 450, 900],
                    ],
                    dur: 1.6,
                    gain: 0.4,
                    vibrato: 4,
                    breath: 0.2,
                });
                break;
            case 'chime':
                this.tone({ freq: 880, dur: 0.5, type: 'sine', gain: 0.25 });
                this.tone({
                    freq: 1318.5,
                    dur: 0.7,
                    type: 'sine',
                    gain: 0.2,
                    delay: 0.14,
                });
                break;
        }
    }
}

export const sound = new SoundEngine();
